import {
  materialCells,
  materialRenderers,
} from "@jsonforms/material-renderers";
import { JsonForms } from "@jsonforms/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import Snackbar from "../../common/Snackbar";
import {
  createFormTemplate,
  updateFormTemplate,
} from "../../redux/formTemplateSlice";
import { buildPermissionContext } from "../../util/permissions";

const FormTemplateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;
  const isEditMode = location.state?.editMode || false;
  const existingTemplate = location.state?.template || {};
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });
  const [templateData, setTemplateData] = useState({
    id: existingTemplate?.id || null,
    form_name: existingTemplate?.form_name || "",
    json_schema: existingTemplate?.json_schema || {
      type: "object",
      properties: {},
    },
    ui_schema: existingTemplate?.ui_schema || {},
    short_description: existingTemplate?.short_description || "",
    form_data: existingTemplate?.form_data || {},
  });

  const [auditData, setAuditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    signature: "",
    action: isEditMode ? "UPDATE_Form_Template" : "CREATE_Form_Template",
    comment: isEditMode
      ? `Updated Form Template: ${existingTemplate.form_name}`
      : `Created Form Template: ${templateData.form_name}`,
    date: new Date().toISOString(),
    parent_version: null,
    version_content: {},
  });

  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) =>
        setAuditData((prev) => ({ ...prev, ip_address: data.ip }))
      )
      .catch((err) => console.error("Failed to fetch IP:", err));
  }, []);

  const handleChange = (e) => {
    setTemplateData({ ...templateData, [e.target.name]: e.target.value });
  };
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleJsonUpload = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);

        if (type === "json_schema") {
          if (!json.type || !json.properties) {
            showSnackbar(
              "Invalid JSON Schema. Ensure it contains 'type' and 'properties'.",
              "error"
            );

            return;
          }
        }

        if (type === "ui_schema") {
          if (typeof json !== "object") {
            showSnackbar(
              "Invalid UI Schema. Ensure it's a valid JSON object.",
              "error"
            );

            return;
          }
        }

        setTemplateData((prev) => ({
          ...prev,
          [type]: json,
        }));
      } catch (error) {
        showSnackbar(
          "Invalid JSON. Please upload a properly formatted JSON file.",
          "error"
        );
      }
    };
    reader.readAsText(file);
  };

  const handleSave = () => {
    const finalData = {
      ...templateData,
      audit_trail_data: {
        ...auditData,
        date: new Date().toISOString(),
      },
    };

    if (isEditMode) {
      dispatch(updateFormTemplate({ id: templateData.id, data: finalData }));
    } else {
      dispatch(createFormTemplate(finalData));
    }
    navigate("/form-templates");
  };

  return (
    <div>
      <AppBar
        title={isEditMode ? "Edit Form Template" : "New Form Template"}
        buttons={[
          {
            label: "Save",
            onClick: canEdit ? handleSave : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save form templates."
              : undefined,
          },
        ]}
      />
      <form className="user-form">
        <div className="form-group">
          <label>Form Name</label>
          <input
            type="text"
            name="form_name"
            value={templateData.form_name}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Upload JSON Schema</label>
          <input
            type="file"
            accept="application/json"
            onChange={(e) => handleJsonUpload(e, "json_schema")}
          />
        </div>

        <div className="form-group">
          <label>Upload UI Schema</label>
          <input
            type="file"
            accept="application/json"
            onChange={(e) => handleJsonUpload(e, "ui_schema")}
          />
        </div>

        <div className="form-group">
          <h6>Preview</h6>
          {Object.keys(templateData.json_schema).length > 0 &&
          Object.keys(templateData.ui_schema).length > 0 ? (
            <JsonForms
              key={JSON.stringify(templateData)}
              schema={templateData.json_schema}
              uischema={templateData.ui_schema}
              data={templateData.form_data}
              renderers={materialRenderers}
              cells={materialCells}
              onChange={({ data }) => {
                setTemplateData((prev) => ({
                  ...prev,
                  form_data: data,
                }));
              }}
            />
          ) : (
            <p>Please upload both a JSON schema and a UI schema.</p>
          )}
        </div>
      </form>
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default FormTemplateForm;
